import { useState, useRef, useCallback, useEffect } from "react";
import { useTheme } from "./App";
import jsQR from "jsqr";


function printPriceLabels(labels) {
  const W_IN = 1.5;
  const H_IN = 0.5;

  const labelCSS = labels.map((l, i) => `
    <div class="label">
      <div class="name">${escHtml(l.CardName || l.name || "")}</div>
      <div class="meta">${escHtml([l.Set || l.set, l.Condition || l.condition].filter(Boolean).join(" · "))}</div>
      <div class="price">${l.ListPrice || l.price ? "$" + Math.round(+(l.ListPrice || l.price)) : "—"}</div>
    </div>
  `).join("");

  const win = window.open("", "_blank");
  win.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>CardVendor Price Labels</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      background: white;
    }
    .page {
      display: flex;
      flex-wrap: wrap;
      width: ${W_IN}in;
      margin: 0;
      padding: 0;
    }
    .label {
      width: ${W_IN}in;
      height: ${H_IN}in;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 1pt 3pt;
      border-bottom: 0.5pt dotted #ccc;
      page-break-inside: avoid;
    }
    .name {
      font-size: 6.5pt;
      font-weight: bold;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.15;
    }
    .meta {
      font-size: 5pt;
      color: #555;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.2;
    }
    .price {
      font-size: 8pt;
      font-weight: bold;
      line-height: 1.2;
    }
    @page {
      size: ${W_IN}in auto;
      margin: 0;
    }
    @media print {
      body { margin: 0; }
      .page { width: ${W_IN}in; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="padding:12px;font-family:Arial;font-size:12px;background:#f0f4ff;border-bottom:1px solid #ccc;">
    <strong>CardVendor Price Labels</strong> — ${labels.length} label${labels.length !== 1 ? "s" : ""}
    &nbsp;·&nbsp;
    <strong>NELKO P21</strong> — export XLSX and upload to NELKO app
    &nbsp;·&nbsp;
    Export XLSX and upload to NELKO P21 app to print all labels at once.
    <button onclick="window.print()" style="margin-left:12px;padding:4px 12px;background:#1d4ed8;color:white;border:none;border-radius:4px;cursor:pointer;font-size:12px;">🖨 Print</button>
  </div>
  <div class="page">
    ${labelCSS}
  </div>
</body>
</html>`);
  win.document.close();
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ─── PRICE LABEL GENERATOR COMPONENT ─────────────────────────────────────────
export function QRLabelGenerator({ inventory, onUpdateItem, onClose }) {
  const { S, T } = useTheme();
  const [search, setSearch]       = useState("");
  const [selected, setSelected]   = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Cards in stock eligible for labels
  const eligible = inventory.filter(i =>
    (i.Status === "In Stock" || i.Status === "Reserved") && i.ListPrice
  );

  const filtered = search.trim()
    ? eligible.filter(i =>
        i.CardName?.toLowerCase().includes(search.toLowerCase()) ||
        i.Set?.toLowerCase().includes(search.toLowerCase()))
    : eligible;

  const noPrice = inventory.filter(i =>
    (i.Status === "In Stock" || i.Status === "Reserved") && !i.ListPrice
  ).length;

  const toggleCard = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelected(new Set());
      setSelectAll(false);
    } else {
      setSelected(new Set(filtered.map(i => i.ID)));
      setSelectAll(true);
    }
  };

  const handlePrint = () => {
    const toPrint = selected.size > 0
      ? eligible.filter(i => selected.has(i.ID))
      : filtered;
    if (toPrint.length === 0) return;
    printPriceLabels(toPrint);
  };

  const printCount = selected.size > 0 ? selected.size : filtered.length;

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.drawer, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 12, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Price Labels</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
              Export XLSX → upload to NELKO P21 app
            </div>
          </div>
          <button style={{ ...S.btn("ghost"), padding: "6px 10px" }} onClick={onClose}>✕</button>
        </div>

        {/* Printer tip */}
        <div style={{ ...S.infoBox, marginBottom: 12, flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.7 }}>
            <div>🖨 Use the <strong style={{ color: T.text }}>NELKO P21</strong> — export XLSX and upload to NELKO app to print labels.</div>
            <div>Each label fits: card name, set · condition, and list price.</div>
          </div>
        </div>

        {/* Search + select all */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexShrink: 0 }}>
          <input style={{ ...S.input, flex: 1, fontSize: 14 }}
            value={search} onChange={e => { setSearch(e.target.value); setSelectAll(false); }}
            placeholder={`Search ${eligible.length} priced cards…`} />
          <button style={{ ...S.btn(selectAll ? "primary" : "ghost"), padding: "8px 12px",
            fontSize: 12, flexShrink: 0 }}
            onClick={handleSelectAll}>
            {selectAll ? "✓ All" : "Select All"}
          </button>
        </div>

        {noPrice > 0 && (
          <div style={{ fontSize: 11, color: "#fbbf24", marginBottom: 8, flexShrink: 0 }}>
            ⚠ {noPrice} card{noPrice !== 1 ? "s" : ""} skipped — no list price set
          </div>
        )}

        {/* Card list */}
        <div style={{ flex: 1, overflowY: "auto", marginBottom: 12 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "30px 0", color: T.textFaint }}>
              {search.trim() ? `No results for "${search}"` : "No priced cards in inventory"}
            </div>
          )}
          {filtered.map(item => (
            <div key={item.ID}
              style={{ display: "flex", alignItems: "center", gap: 10,
                padding: "8px 0", borderBottom: `0.5px solid ${T.border}`,
                cursor: "pointer" }}
              onClick={() => toggleCard(item.ID)}>
              {/* Checkbox */}
              <div style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${selected.has(item.ID) ? "#3b82f6" : T.border}`,
                background: selected.has(item.ID) ? "#3b82f6" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {selected.has(item.ID) && (
                  <span style={{ color: "white", fontSize: 12, lineHeight: 1 }}>✓</span>
                )}
              </div>

              {/* Label preview */}
              <div style={{ flex: 1, minWidth: 0,
                background: T.surface2, borderRadius: 4,
                padding: "3px 8px", border: `0.5px solid ${T.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.CardName}
                </div>
                <div style={{ fontSize: 10, color: T.textMuted,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {[item.Set, item.Condition].filter(Boolean).join(" · ")}
                </div>
              </div>

              {/* Price */}
              <div style={{ fontSize: 14, fontWeight: 700, color: "#88bdf2",
                flexShrink: 0, minWidth: 44, textAlign: "right" }}>
                ${Math.round(item.ListPrice)}
              </div>
            </div>
          ))}
        </div>

        {/* Print button */}
        <button
          style={{ ...S.btn("primary"), width: "100%", padding: "13px",
            fontSize: 15, flexShrink: 0 }}
          onClick={handlePrint}
          disabled={printCount === 0}>
          🖨 Print {printCount} Label{printCount !== 1 ? "s" : ""}
        </button>

      </div>
    </div>
  );
}

// ─── QR SCANNER COMPONENT ─────────────────────────────────────────────────────
export function QRScanner({ onScan, onFail, onClose }) {
  const { S, T } = useTheme();
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError]     = useState("");
  const [scanning, setScanning] = useState(false);
  const streamRef = useRef(null);
  const rafRef    = useRef(null);

  const startCamera = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: 640, height: 480 }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setScanning(true);
        scanFrame();
      }
    } catch (e) {
      setError("Camera access denied. Please allow camera access and try again.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setScanning(false);
  }, []);

  const scanFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    try {
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code && code.data) {
        stopCamera();
        onScan(code.data);
        return;
      }
    } catch (e) {
      // scan error, continue
    }

    rafRef.current = requestAnimationFrame(scanFrame);
  }, [onScan, stopCamera]);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div style={{ ...S.overlay }} onClick={e => e.target === e.currentTarget && handleClose()}>
      <div style={{ ...S.drawer, padding:"16px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <div style={{ fontSize:16, fontWeight:600 }}>Scan Card QR Code</div>
          <button style={{ ...S.btn("ghost"), padding:"6px 10px" }} onClick={handleClose}>✕</button>
        </div>

        {error ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📷</div>
            <div style={{ fontSize:13, color:"#f87171", marginBottom:16 }}>{error}</div>
            <button style={{ ...S.btn("primary") }} onClick={startCamera}>Try Again</button>
          </div>
        ) : (
          <>
            <div style={{ position:"relative", borderRadius:12, overflow:"hidden",
              background:"#000", marginBottom:12 }}>
              <video ref={videoRef} style={{ width:"100%", display:"block" }}
                playsInline muted />
              {/* Scanning overlay */}
              <div style={{ position:"absolute", inset:0, display:"flex",
                alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                <div style={{ width:200, height:200, border:"2px solid #3b82f6",
                  borderRadius:12, boxShadow:"0 0 0 9999px rgba(0,0,0,0.4)" }}>
                  {/* Corner marks */}
                  {[
                    {top:0,left:0,borderTop:"3px solid #3b82f6",borderLeft:"3px solid #3b82f6"},
                    {top:0,right:0,borderTop:"3px solid #3b82f6",borderRight:"3px solid #3b82f6"},
                    {bottom:0,left:0,borderBottom:"3px solid #3b82f6",borderLeft:"3px solid #3b82f6"},
                    {bottom:0,right:0,borderBottom:"3px solid #3b82f6",borderRight:"3px solid #3b82f6"},
                  ].map((style,i) => (
                    <div key={i} style={{ position:"absolute", width:20, height:20, borderRadius:2, ...style }} />
                  ))}
                </div>
              </div>
            </div>
            <canvas ref={canvasRef} style={{ display:"none" }} />
            <div style={{ fontSize:12, color:T.textMuted, textAlign:"center" }}>
              {scanning ? "Point camera at a CardVendor QR label" : "Starting camera…"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
